<template>
  <div class="rhg-instructions-map__instructions-container">
    <div v-if="routeSteps">
      <div class="rhg-instructions-map__instructions-header">
        <div class="rhg-instructions-map__header-text-wrapper">
          <i class="rhg-font-icon--located"></i>
          <p class="rhg-instructions-map__text-header"><strong>{{ originName }}</strong></p>
        </div>
        <div>
          <label for="unit-system-change">
            <input
              id="unit-system-change"
              class="rhg-instructions-map__unit-system-input"
              type="checkbox"
              name="unitSystem"
              @click="handleUnitSystemChange"
              :checked="isMilesOptionChecked"
            >
            <div class="rhg-instructions-map__unit-selector">
              <span class="rhg-instructions-map__unit-option" :class="{ 'active': !isMilesOptionChecked }">{{translate('weKms')}}</span>
              <span> | </span>
              <span class="rhg-instructions-map__unit-option" :class="{ 'active': isMilesOptionChecked }">{{translate('weMi')}}</span>
            </div>
          </label>
        </div>
      </div>
      <ul class="rhg-instructions-map__instructions-wrapper" @scroll="handleScroll">
        <li class="rhg-instructions-map__list-item" v-for="(routeStep, i) in routeSteps.steps" :key="i">
          <div class="rhg-instructions-map__instructions">
            <i :class="`maneuver rhg-font-icon--${routeStep.maneuver}`"></i>
            <div v-html="parseRichText(routeStep.instructions)"></div>
          </div>
          <span class="rhg-instructions-map__distance">{{ getDistance(routeStep.distance.value )}}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Instructions',
  props: {
    routeSteps: Object,
    originName: String,
    unitSystemOptions: Object
  },
  data: () => ({
    isMilesOptionChecked: false,
    unitSystem: 0
  }),
  methods: {
    handleScroll(e) {
      e.target.classList.add('scrolling')

      setTimeout(() => {
        e.target.classList.remove('scrolling')
      }, 300)
    },
    parseRichText (text) {
      return text.replace('<b>', '<strong>').replace('</b>', '</strong>')
    },
    getDistance(distanceInKm) {
      let distance
      if (this.unitSystem === this.unitSystemOptions.METRIC) {
        distance = distanceInKm / 1000
      } else {
        distance = distanceInKm / 1609
      }

      distance = parseFloat(distance.toFixed(1))

      if (distance < 0.1) {
        distance = '< 0.1'
      }

      return this.unitSystem === this.unitSystemOptions.METRIC
        ? this.translate('desKM', undefined, distance)
        : this.translate('desMileAbbr', undefined, distance)
    },
    handleUnitSystemChange() {
      this.isMilesOptionChecked = !this.isMilesOptionChecked
      this.unitSystem = !this.isMilesOptionChecked
        ? this.unitSystemOptions.METRIC
        : this.unitSystemOptions.IMPERIAL
      this.$emit('onUnitSystemSelection', this.unitSystem)
    }
  }
}
</script>

<style lang="scss" scoped>
.rhg-instructions-map {
  &__instructions-container {
    height: 452px;
    border-top: 1px solid rgba(color('black-light'), 0.3);

    @media (min-width: 992px) {
      height: 667px;
      border-top: 1px solid color('light-grey');
      border-bottom: unset;
    }
  }

  &__unit-selector {
    display: flex;
    gap: 12px;
    font-size: 14px;
    line-height: 20px;
    text-transform: lowercase;

    .active {
      font-weight: 700;
    }
  }

  &__instructions-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 32px;
    padding: 24px 16px;

    @media (min-width: 992px) {
      padding: 22px 24px;
    }

    i[class*="rhg-font-icon--"] {
      height: 27px;
      width: 27px;
      font-size: 27px;

      @media (min-width: 992px) {
        height: 24px;
        width: 24px;
        font-size: 24px;
      }
    }
  }

  &__text-header {
    margin: 0;
    font-size: 14px;
    line-height: 20px;

    @media (min-width: 992px) {
      font-size: 16px;
      line-height: 19px;
    }
  }

  &__header-text-wrapper {
    display: flex;
    align-items: center;
    gap: 19px;

    @media (min-width: 992px) {
      gap: 24px;
    }
  }

  &__instructions-wrapper {
    max-height: 377px;
    overflow: auto;
    padding: 0 16px 0 16px;
    scrollbar-color: color('medium-grey') transparent;
    scrollbar-width: thin;

    @media (min-width: 992px) {
      padding: 0 24px 0 24px;
      max-height: 599px;
    }

    &::-webkit-scrollbar {
      width: 7px;
      border-radius: 100vw;
      cursor: pointer !important;
    }
    &::-webkit-scrollbar-track {
      border-radius: 100vw;
      background: none;
      margin-block: 5px;
      cursor: pointer !important;
    }
    &::-webkit-scrollbar-thumb {
      border: 1px #fff solid;
      background-color: color('medium-grey');
      border-radius: 100vw;
      margin-inline-start: 3px;
      margin-inline-end: 7px;
      cursor: pointer !important;
    }

    &.scrolling::-webkit-scrollbar-thumb {
      border: 1px #fff solid;
      background-color: color('black-discovery');
      border-radius: 100vw;
      margin-inline-start: 3px;
      margin-inline-end: 7px;
      cursor: pointer !important;
    }

    &.scrolling {
      scrollbar-color: color('black-discovery') transparent;
      scrollbar-width: thin;
    }
  }

  &__list-item {
    display: flex;
    text-align: start;
    flex-direction: column;
    gap: 4px;
    justify-content: space-between;
    border-top: 1px solid rgb(217, 217, 217);
    padding: 12px 0;

    @media (min-width: 992px) {
      border-top: 1px solid color('light-grey');
      padding: 20px 0;
      flex-direction: row;
      gap: 24px;
    }
  }

  &__instructions {
    display: flex;
    gap: 20px;
    color: color('black-light');

    div {
      font-size: 14px;
      line-height: 20px;
      align-self: center;

      @media (min-width: 992px) {
        font-size: 16px;
        line-height: 15px;
      }

      div, b, strong {
        /* Google fives instructions with inline styling. Important to override it. */
        font-size: inherit !important;
        line-height: inherit !important;
      }
    }
  }

  &__distance {
    font-size: 14px;
    line-height: 20px;
    color: color('dark-grey');
    text-transform: lowercase;
    margin-inline-start: 44px;

    @media (min-width: 992px) {
      font-size: 14px;
      line-height: 13px;
      color: color('black-discovery');
      align-self: center;
      margin-inline-start: unset;
    }
  }

  &__unit-system-input {
    position: absolute;
  }

  &__unit-option {
    cursor: pointer;

    &:hover {
      font-weight: 700;
    }
  }
}

i.maneuver {
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  font-size: 24px;
  color: color('black-discovery');

  @media (min-width: 992px) {
    color: color('black-light');
  }
}

/* HIDE RADIO AND CHECKBOX only iOs*/
@supports (-webkit-touch-callout: none) {
  input[type='radio'],
  input[type='checkbox'] {
    margin: 0 !important;
    padding: 0 !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    width: 0;
    height: 0;
    border: 0;
  }
}
</style>
